import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { getStoredSessionUser, persistSessionUser, resolveCurrentDoctor, resolveCurrentPetOwner } from '../utils/currentUserResolvers';
import { adminApi, authApi, subscriptionApi, tokenStorage } from '../shared/services/api';

export const AuthContext = createContext();

const SUBSCRIPTION_POLL_ATTEMPTS = 5;
const SUBSCRIPTION_POLL_INTERVAL_MS = 2000;
const PENDING_SUBSCRIPTION_STATUSES = new Set(['pending_activation']);
const SUBSCRIPTION_BYPASS_ROLES = new Set(['super_admin', 'platform_staff', 'pet_owner']);
const EMPTY_SUBSCRIPTION_STATE = {
  status: null,
  plan: null,
  usage: {},
  limits: {},
  warnings: {},
  exceeded: {},
  loading: false
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [subscriptionState, setSubscriptionState] = useState(EMPTY_SUBSCRIPTION_STATE);

  const applySession = (token, nextUserData, refreshToken) => {
    tokenStorage.setSession({
      accessToken: token,
      refreshToken,
      user: nextUserData
    });

    localStorage.setItem('profile_type', nextUserData.profile_type || nextUserData.role || '');
    localStorage.setItem('profile_id', nextUserData.profile_id || nextUserData.user_id || '');
    localStorage.setItem('hospital_id', nextUserData.hospital_id || '');
    localStorage.setItem('management_id', nextUserData.management_id || '');
    localStorage.setItem('doctor_id', nextUserData.doctor_id || '');
    localStorage.setItem('ownerId', nextUserData.pet_owner_id || nextUserData.petOwnerId || '');
    localStorage.setItem('petOwnerId', nextUserData.pet_owner_id || nextUserData.petOwnerId || '');
    localStorage.setItem('pet_owner_id', nextUserData.pet_owner_id || nextUserData.petOwnerId || '');

    setAccessToken(token);
    setUserData(nextUserData);
    setUserLoggedIn(true);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('accessToken');
      const storedUserData = getStoredSessionUser();

      if (token) {
        setAccessToken(token);
        setUserLoggedIn(true);
      }

      if (Object.keys(storedUserData).length) {
        try {
          setUserData(storedUserData);
        } catch (error) {
          localStorage.removeItem('loginData');
        }
      }
    }

    setSessionReady(true);

    const handleSessionCleared = () => {
      setAccessToken(null);
      setUserData(null);
      setUserLoggedIn(false);
      setSubscriptionState(EMPTY_SUBSCRIPTION_STATE);
    };

    window.addEventListener('vetcastle:session-cleared', handleSessionCleared);
    return () => window.removeEventListener('vetcastle:session-cleared', handleSessionCleared);
  }, []);

  useEffect(() => {
    let isActive = true;

    const syncDerivedEntityIds = async () => {
      if (!accessToken || !userData?.role) {
        return;
      }

      try {
        if (userData.role === 'doctor' && !userData.doctor_id) {
          const doctor = await resolveCurrentDoctor({ token: accessToken, userData });

          if (doctor?.id && isActive) {
            const nextUser = persistSessionUser({
              doctor_id: doctor.id,
              hospital_id: doctor.hospital_id || userData.hospital_id
            });
            setUserData(nextUser);
          }
        }

        if (userData.role === 'pet_owner' && !userData.pet_owner_id && !userData.petOwnerId) {
          const owner = await resolveCurrentPetOwner({ token: accessToken, userData });

          if (owner?.id && isActive) {
            const nextUser = persistSessionUser({
              pet_owner_id: owner.id,
              petOwnerId: owner.id,
              hospital_id: owner.hospital_id || userData.hospital_id
            });
            setUserData(nextUser);
          }
        }
      } catch (error) {
        console.error('Failed to resolve current user entity IDs:', error);
      }
    };

    syncDerivedEntityIds();

    return () => {
      isActive = false;
    };
  }, [accessToken, userData]);

  const refreshSubscriptionState = useCallback(async () => {
    const role = String(userData?.role || userData?.profile_type || '').toLowerCase();

    if (!accessToken || !userLoggedIn || SUBSCRIPTION_BYPASS_ROLES.has(role)) {
      setSubscriptionState(EMPTY_SUBSCRIPTION_STATE);
      return EMPTY_SUBSCRIPTION_STATE;
    }

    setSubscriptionState((current) => ({
      ...current,
      loading: true
    }));

    try {
      for (let attempt = 0; attempt < SUBSCRIPTION_POLL_ATTEMPTS; attempt += 1) {
        const subscription = await subscriptionApi.getSubscriptionStatus();
        const nextState = {
          status: String(subscription?.status || '').trim().toLowerCase() || 'pending_activation',
          plan: subscription?.plan || null,
          usage: subscription?.usage || {},
          limits: subscription?.limits || {},
          warnings: subscription?.warnings || {},
          exceeded: subscription?.exceeded || {},
          loading: false
        };

        setSubscriptionState(nextState);

        if (!PENDING_SUBSCRIPTION_STATUSES.has(nextState.status) || attempt === SUBSCRIPTION_POLL_ATTEMPTS - 1) {
          return nextState;
        }

        await new Promise((resolve) => window.setTimeout(resolve, SUBSCRIPTION_POLL_INTERVAL_MS));
      }
    } catch (error) {
      console.error('Failed to resolve subscription status:', error);
      const nextState = {
        ...EMPTY_SUBSCRIPTION_STATE,
        loading: false
      };
      setSubscriptionState(nextState);
      return nextState;
    }

    return EMPTY_SUBSCRIPTION_STATE;
  }, [accessToken, userData, userLoggedIn]);

  useEffect(() => {
    let isActive = true;

    const syncSubscriptionStatus = async () => {
      try {
        const nextState = await refreshSubscriptionState();
        if (!isActive) {
          return;
        }
        setSubscriptionState(nextState);
      } catch (error) {
        console.error('Failed to sync subscription status:', error);
      }
    };

    syncSubscriptionStatus();

    return () => {
      isActive = false;
    };
  }, [refreshSubscriptionState]);

  const login = (token, nextUserData) => {
    if (!token || !nextUserData) {
      console.error('Login failed: Missing token or userData');
      return;
    }

    applySession(token, nextUserData, nextUserData.refreshToken || undefined);
  };

  const startImpersonation = ({ token, user }) => {
    if (!token || !user) {
      return;
    }

    if (!tokenStorage.hasImpersonationOrigin()) {
      tokenStorage.saveImpersonationOrigin({
        accessToken: tokenStorage.getAccessToken(),
        refreshToken: tokenStorage.getRefreshToken(),
        user: tokenStorage.getSessionUser() || userData
      });
    }

    applySession(token, user, null);
  };

  const exitImpersonation = async () => {
    const originalSession = tokenStorage.getImpersonationOrigin();

    if (!originalSession) {
      return false;
    }

    try {
      await adminApi.exitImpersonation();
    } catch (error) {
      console.error('Exit impersonation request failed:', error);
    }

    applySession(
      originalSession.accessToken,
      originalSession.user,
      originalSession.refreshToken || null
    );
    tokenStorage.clearImpersonationOrigin();

    return true;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout request failed:', error);
      tokenStorage.clearSession();
      tokenStorage.clearImpersonationOrigin();
    }

    setAccessToken(null);
    setUserData(null);
    setUserLoggedIn(false);

    if (typeof window !== 'undefined') {
      window.location.replace('/signin');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        userLoggedIn,
        userData,
        sessionReady,
        refreshSubscriptionState,
        subscriptionState,
        login,
        logout,
        startImpersonation,
        exitImpersonation
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
